/**
 * PlayerStatsService - caricamento file e statistiche su PocketBase
 */

import { fetchPlayerStats as fetchPlayerStatsPB, savePlayerStats as savePlayerStatsPB } from './db/players.pocketbase.js';
import { getPocketBase } from './pocketbase.js';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities.js';

/** Ultima stagione completa usata per Partite/Gol/Assist/Gialli/Rossi in lista */
export const CURRENT_SEASON_CODE = '2025-26';
export const CURRENT_SEASON_LABEL = '25/26';

export class PlayerStatsService {
  constructor(userSession) {
    this.userSession = userSession;
    console.log('[STATS_SERVICE] ⚡ Servizio inizializzato (PocketBase)');
  }

  async loadPlayersFromFile(file) {
    try {
      if (!file) {
        throw new Error('Nessun file selezionato');
      }

      const ext = (file.name.split('.').pop() || '').toLowerCase();

      if (ext === 'csv' || ext === 'txt') {
        return await this._csvToObjects(await file.text());
      }
      if ((ext === 'xlsx' || ext === 'xls') && typeof window.XLSX !== 'undefined') {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = window.XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames.find(name => name && name.toLowerCase() === 'tutti') || workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rawData = window.XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: '', header: 1 });

        if (rawData.length >= 2) {
          const headers = rawData[1];
          const dataRows = rawData.slice(2);
          const objectData = dataRows.map(row => {
            const obj = {};
            headers.forEach((header, index) => {
              if (header && header.trim()) {
                obj[header.trim()] = row[index] || '';
              }
            });
            return obj;
          });
          return this._normalizePlayerRows(objectData);
        }
        return [];
      }
      throw new Error('Formato non supportato. Usa .csv o .xlsx con SheetJS.');
    } catch (error) {
      console.error('[STATS_SERVICE] Load players from file error:', error);
      throw new Error(`Errore caricamento file: ${error.message}`);
    }
  }

  _csvToObjects(csvText) {
    const lines = csvText.replace(/\r/g, '').split('\n');
    const headers = lines[0].split(',');
    const result = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;
      const values = line.split(',');
      const obj = {};
      headers.forEach((header, index) => {
        if (header && header.trim()) {
          obj[header.trim()] = values[index] || '';
        }
      });
      result.push(obj);
    }

    return this._normalizePlayerRows(result);
  }

  _normalizePlayerRows(rawData) {
    const normalized = rawData.map((row, index) => {
      const player = {
        id: this._extractField(row, ['Id', 'id', 'ID', 'cod', 'codice']) || index,
        name: decodeHtmlEntities(
          this._extractField(row, ['Nome', 'nome', 'giocatore', 'player', 'nome e cognome']) || 'Sconosciuto',
        ),
        role: this._extractField(row, ['RM', 'ruoli', 'Ruolo', 'ruolo', 'R', 'r']) || '',
        team: decodeHtmlEntities(
          this._extractField(row, ['Squadra', 'squadra', 'team', 'club']) || '',
        ),
        quot: this._parseNumber(this._extractField(row, ['Qt', 'quotazione', 'prezzo', 'valore', 'Qt.A'])),
        // FVM = valore quotazioni (non è FantaMedia); FM colonna solo da player_stats 25/26
        fvm: this._parseNumber(this._extractField(row, ['FVM', 'fvm'])),
        fm_score: null,
        // Partite/Gol/Assist/cartellini: solo da player_stats stagione CURRENT_SEASON_CODE (hydrate)
        matches: null,
        goals: null,
        assists: null,
        yellow_cards: null,
        red_cards: null,
        has_season_stats: false,
      };

      if (player.role) {
        const roles = player.role.split(/[,\/;]/).map(r => r.trim()).filter(Boolean);
        player.roles = roles.length > 0 ? roles : [''];
      } else {
        player.roles = [''];
      }

      player.quotazione = player.quot;
      return player;
    }).filter(player => player.name !== 'Sconosciuto' && player.name.trim());

    return normalized;
  }

  _extractField(row, aliases) {
    for (const alias of aliases) {
      if (row[alias] !== undefined && row[alias] !== '') {
        return row[alias];
      }
    }
    return null;
  }

  _parseNumber(value) {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;
    const cleaned = String(value).replace(',', '.').replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  _checkAuth() {
    if (!this.userSession.isAuthenticated()) {
      throw new Error('Utente non autenticato');
    }
    return this.userSession.accessKey || this.userSession.getAccessCode?.();
  }

  async savePlayerStats(players, seasonCode = CURRENT_SEASON_CODE) {
    const accessKey = this._checkAuth();
    return savePlayerStatsPB({ accessKey, seasonCode, players });
  }

  async loadPlayerStats(seasonCode = null) {
    const data = await fetchPlayerStatsPB({ seasonCode });
    console.log('[STATS_SERVICE] giocatori da PocketBase:', data.length);
    return { success: true, data };
  }

  async loadPlayersFromOnline(deps = {}) {
    try {
      const base = deps.baseUrl || getPocketBase().baseUrl;
      const doFetch = deps.fetchImpl || ((...a) => fetch(...a));
      const url = String(base).replace(/\/$/, '') + '/api/fc/quotazioni';

      const res = await doFetch(url);
      if (!res || !res.ok) {
        return { success: false, error: 'Errore nel recupero delle quotazioni online' };
      }
      const data = await res.json();
      const rows = (data && data.players) || [];
      if (!rows.length) {
        return { success: false, error: 'Nessun giocatore ricevuto da Fantacalcio' };
      }

      const players = this._normalizePlayerRows(rows);
      const withStats = players.filter((p) => p.fvm > 0).length;
      return { success: true, data: { players, withStats } };
    } catch (error) {
      console.error('[STATS_SERVICE] Errore caricamento online:', error);
      return { success: false, error: error.message };
    }
  }

  async processExcelFile(file) {
    try {
      const players = await this.loadPlayersFromFile(file);
      if (!players || players.length === 0) {
        return { success: false, error: 'Nessun giocatore valido trovato nel file' };
      }
      const withStats = players.filter(p => p.fvm > 0).length;
      return { success: true, data: { players, withStats } };
    } catch (error) {
      return { success: false, error: error.message || 'Errore durante il processamento del file' };
    }
  }
}

export default PlayerStatsService;
